import { A } from '@adornis/base/env-info';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity';
import { getCollection } from '@adornis/baseql/server/collections';
import { Message, Recipient } from '@adornis/mails/server/communication';
import { AdornisUser } from '@adornis/users/db/a-user';
import { getPasswordResetLink, getValidationLink } from '@adornis/users/server/a-userSecrets';
import { validate } from '@adornis/validation/decorators';
import { nonOptional } from '@adornis/validation/functions/nonOptional';
import { DateTime } from 'luxon';
import { confirmEmail, mailer } from 'server/mailer';
import { passwordResetEmail } from './../server/mailer';
import { universityList } from './verified-domains-list';

export enum EDUCATION {
  STUDENT = 'student',
  AUPAIR = 'AuPair',
  SPRACHSTUDENT = 'sprachstudent',
  YOUNG_PROFESSIONAL = 'young_professional',
  OTHER = 'other',
}

@Entity()
export class UserCreationEntity extends AdornisEntity {
  public static _class = 'UserCreationEntity';

  // @validate(nonOptional())
  @Field(type => String)
  public name!: string;

  // @validate(nonOptional())
  @Field(type => String)
  public country!: string;

  @Field(type => String)
  public cityInGermany?: string;

  @Field(type => DateTime)
  public arrival?: DateTime;

  @Field(type => String)
  public education?: string;

  @Field(type => String)
  public university?: string;

  @Field(type => String)
  public degree?: string;

  @Field(type => String)
  public program?: string;

  @Field(type => String)
  public universityMail?: string;

  @validate((val, fieldName, entity: UserCreationEntity) => {
    if (entity.education === EDUCATION.OTHER) nonOptional()(val, fieldName, entity);
  })
  @Field(type => String)
  public otherEducation?: string;

  // @validate(nonOptional()) @Field(type => PhoneField) public phonenumber!: PhoneField;

  @Field(type => String) public randomKeyToActivateEnter?: any; // only to avoid errors from a-form and to enable pressing enter on pw repeat input
  @Field(type => Boolean) public acceptedNewsEmails?: boolean; // TODO Später
}

/**
 * @entity WTGUser
 */
@Entity()
export class WTGUser extends AdornisUser {
  public static _class = 'WTGUser';

  @Field(type => String)
  public firstname!: string;

  @Field(type => String)
  public lastname!: string;

  public get displayName() {
    return `${this.firstname || 'Hello!'}`;
  }

  @Field(type => Boolean)
  public hasVerifiedUniversityMembership!: boolean;

  @Field(type => Boolean)
  public hasOnboarded!: boolean;

  @Field(type => UserCreationEntity, { default: val => new UserCreationEntity({}) })
  public userData?: UserCreationEntity;

  public static isUniversityEmail(email: string) {
    const domains = universityList.flatMap(uniInfo => uniInfo.domains);
    return domains.some(domain => email.endsWith(domain));
  }

  @Mutation(type => String)
  public static registerWTGUser(
    @Arg('username', type => String) username: string,
    @Arg('email', type => String) email: string = '',
    @Arg('password', type => String) password: string,
  ) {
    return async (gqlFields: '') => {
      const id = await super.registerUser(username, email, password, {});
      await this.sendInviteMail(id)('');
      return id;
    };
  }

  @Query(type => Boolean)
  public static sendInviteMail(@Arg('userID', type => String) userID: string) {
    return async (gqlFields: string) => {
      if (!A.isServer) throw new Error('only accessible on server');
      const user = await WTGUser.getByID<WTGUser>(userID)(WTGUser.allFields);
      if (!user) throw new Error("User doesn't exist");
      const inviteLink = await getValidationLink(user._id, '/api/verify'); // war davor /blog, ??
      await mailer.sendMail(
        new Message({
          replyTo: 'test@adornis.de',
          html: await confirmEmail.assembleToString({ inviteLink }),
          subject: `Bestätigung Ihrer Email`,
        }),
        new Recipient(user.email),
      );
    };
  }

  @Mutation(type => Boolean)
  public static requestChangePassword(@Arg('email', type => String) email: string) {
    return async () => {
      const user = await (await getCollection<WTGUser>(WTGUser._collectionName)).findOne({ email });
      if (!user) throw new Error(`user ${email} not found`);
      mailer.sendMail(
        new Message({
          html: await passwordResetEmail.assembleToString({
            name: user.displayName ?? 'Welcome To Germany User',
            link: await getPasswordResetLink(user?._id, '/change-password'),
          }),
          subject: 'Your Password Reset',
        }),
        new Recipient(user.email),
      );
    };
  }
}

if (A.isDevelopment) globalThis.WTGUser = WTGUser;
